import { clampClaimQuantity } from '../../../../Component/UI/orderClaimProgress';
import styled from 'styled-components';
import { Order_detail } from '../../../../Component/Interface/Order';

export function automaticRefundAmount(detail: Pick<Order_detail, 'product_price' | 'refund_unit_settlement' | 'refund_unit_offset'>, quantity: number) {
  if (!detail.refund_unit_settlement) return quantity * detail.product_price;
  let offset = detail.refund_unit_offset || 0, remaining = quantity, amount = 0;
  for (const run of JSON.parse(detail.refund_unit_settlement) as { count: number; net: number }[]) {
    const skip = Math.min(offset, run.count); offset -= skip;
    const take = Math.min(remaining, run.count - skip); remaining -= take; amount += take * run.net;
  }
  return amount;
}

export type RefundAmountItem = { seq: number; name: string; quantity: number; maximum: number; maxQuantity?: number };
export const validRefundAmounts = (items: RefundAmountItem[], values: Record<number, string>) => items.every(item => {
  const value = values[item.seq];
  return value === undefined || value === '' || (Number.isSafeInteger(Number(value)) && Number(value) >= 0 && Number(value) <= item.maximum);
});

export default function RefundAmountFields({ items, values, onChange, onQuantityChange, disabled }: {
  items: RefundAmountItem[]; values: Record<number, string>; onChange: (seq: number, value: string) => void; disabled?: boolean;
  onQuantityChange?: (seq: number, quantity: number) => void;
}) {
  return <Fields>
    <p>환불 수량에 맞춰 실결제 금액을 자동 계산합니다. 금액을 직접 수정할 수 있으며 배송비는 별도로 적용됩니다.</p>
    {items.map(item => <div key={item.seq}>
      <strong className="refund-product-name">{item.name}</strong>
      {onQuantityChange && <label>환불 수량
        <input type="number" aria-label={`${item.name} 환불 수량`} min={1} max={item.maxQuantity} step={1} value={item.quantity}
          disabled={disabled} onChange={event => onQuantityChange(item.seq, Number(clampClaimQuantity(event.target.value, item.maxQuantity ?? item.quantity, 1)))} />
      </label>}
      <label>상품 환불 금액
      <input type="number" aria-label={`${item.name} 상품 환불 금액`} min={0} max={item.maximum} step={1}
        value={values[item.seq] ?? String(item.maximum)} disabled={disabled}
        onChange={event => onChange(item.seq, event.target.value)} />
      <small>자동 계산 금액 {item.maximum.toLocaleString('ko-KR')}원 · 이 금액 이내에서 수정 가능</small>
      </label>
    </div>)}
    {!validRefundAmounts(items, values) && <p role="alert">환불 금액은 선택한 수량의 금액 이내에서 원 단위로 입력해주세요.</p>}
  </Fields>;
}

const Fields = styled.div`
  display: grid; gap: var(--space-3); margin-bottom: var(--space-4);
  p, small { margin: 0; color: var(--color-text-secondary); font-size: var(--text-caption); line-height: var(--leading-body); }
  label { display: grid; align-content: start; gap: var(--space-2); }
  > div { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 2fr); gap: var(--space-3); padding: var(--space-4) 0; border-bottom: 1px solid var(--color-border-default); }
  .refund-product-name { grid-column: 1 / -1; overflow-wrap: anywhere; font-size: var(--text-body); font-weight: var(--font-weight-medium); }
  input { width: 100%; font-variant-numeric: tabular-nums; }
  @media (max-width: 480px) { > div { grid-template-columns: minmax(0, 1fr); } }
  input { min-height: var(--control-lg); box-sizing: border-box; padding: 0 var(--space-3); border: 1px solid var(--color-border-default);
    border-radius: var(--radius-md); color: var(--color-text-primary); background: var(--color-bg-surface); font: inherit; }
  input:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
  [role="alert"] { color: var(--color-text-danger); }
`;
